import ShopifyClient from 'files/ShopifyClient.js';

const CART_STORAGE_KEY = 'aqua_link_cart';

const Cart = {

    // In-memory checkout reference
    _checkout: null,
    _listeners: [],

    // Initialize cart (create or restore Shopify checkout)
    init: async () => {
        try {
            let storedCartId = localStorage.getItem(CART_STORAGE_KEY);
            if(storedCartId) {
                try {
                    let cart = await ShopifyClient.fetchCheckout(storedCartId);
                    if(cart) {
                        Cart._checkout = cart;
                        return cart;
                    }
                } catch(e) {
                    // Stored cart is invalid, create new one
                    localStorage.removeItem(CART_STORAGE_KEY);
                }
            }

            let cart = await ShopifyClient.createCheckout();
            if(cart) {
                localStorage.setItem(CART_STORAGE_KEY, cart.id);
                Cart._checkout = cart;
            }
            return cart;
        } catch(e) {
            console.error('Error initializing cart:', e);
            return null;
        }
    },

    // Get the current checkout
    get: () => {
        return Cart._checkout;
    },

    // Get line items in the cart
    getItems: () => {
        if(!Cart._checkout) {
            return [];
        }
        return Cart._checkout.lineItems || [];
    },

    // Get the number of items in the cart
    getItemCount: () => {
        if(!Cart._checkout) {
            return 0;
        }
        return Cart._checkout.lineItems?.reduce((total, item) => total + item.quantity, 0) || 0;
    },

    // Get the subtotal
    getSubtotal: () => {
        if(!Cart._checkout) {
            return '0.00';
        }
        return Cart._checkout.subtotalPrice?.amount || Cart._checkout.subtotalPrice || '0.00';
    },

    // Add an item to the cart
    addItem: async (variantId, quantity = 1) => {
        try {
            if(!Cart._checkout) {
                await Cart.init();
            }
            let checkout = await ShopifyClient.addToCheckout(Cart._checkout.id, [{
                variantId: variantId,
                quantity: quantity
            }]);
            Cart._checkout = checkout;
            Cart._notifyListeners();
            return checkout;
        } catch(e) {
            console.error('Error adding item to cart:', e);
            return null;
        }
    },

    // Update an item quantity in the cart
    updateItem: async (lineItemId, quantity) => {
        try {
            let checkout = await ShopifyClient.updateCheckout(Cart._checkout.id, [{
                id: lineItemId,
                quantity: quantity
            }]);
            Cart._checkout = checkout;
            Cart._notifyListeners();
            return checkout;
        } catch(e) {
            console.error('Error updating cart item:', e);
            return null;
        }
    },

    // Remove an item from the cart
    removeItem: async (lineItemId) => {
        try {
            let checkout = await ShopifyClient.removeFromCheckout(Cart._checkout.id, [lineItemId]);
            Cart._checkout = checkout;
            Cart._notifyListeners();
            return checkout;
        } catch(e) {
            console.error('Error removing cart item:', e);
            return null;
        }
    },

    // Get the Shopify checkout URL
    getCheckoutUrl: () => {
        return ShopifyClient.getCheckoutUrl(Cart._checkout);
    },

    // Subscribe to cart changes
    subscribe: (listener) => {
        Cart._listeners.push(listener);
        return () => {
            Cart._listeners = Cart._listeners.filter(l => l !== listener);
        };
    },

    // Notify all listeners of a change
    _notifyListeners: () => {
        Cart._listeners.forEach(listener => {
            try {
                listener(Cart._checkout);
            } catch(e) {
                console.error('Error notifying cart listener:', e);
            }
        });
    }
};

export default Cart;
